import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Truck, X, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShiftEquipmentPage = () => {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    shift_date: today,
    job_id: "",
    primary_machine_id: "",
    secondary_machine_ids: [],
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, m, j] = await Promise.all([
        axios.get(`${API}/mcs/shift-equipment`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/jobs?status=active`),
      ]);
      setItems(r.data || []);
      setMachines(m.data || []);
      setJobs(j.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSecondary = (id) => {
    setForm(f => ({
      ...f,
      secondary_machine_ids: f.secondary_machine_ids.includes(id)
        ? f.secondary_machine_ids.filter(x => x !== id)
        : [...f.secondary_machine_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.shift_date) return toast.error("Shift date required");
    if (!form.primary_machine_id && form.secondary_machine_ids.length === 0) return toast.error("Pick at least one machine");
    setSaving(true);
    try {
      await axios.post(`${API}/mcs/shift-equipment`, form);
      toast.success("Equipment recorded");
      setShowCreate(false);
      setForm({ shift_date: today, job_id: "", primary_machine_id: "", secondary_machine_ids: [], notes: "" });
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="shift-equipment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight text-slate-900 font-heading">Multi-Machine Shift</h1>
          <p className="text-sm text-slate-500 mt-1">Tag every machine you used today (ute + conveyor + EWP) against one shift.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold" data-testid="create-shift-equipment-btn">
          <Plus className="w-5 h-5" /> New Shift
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-md text-slate-400">No shifts recorded yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(it => (
            <div key={it.id} className="bg-white border-2 border-slate-200 rounded-md p-4" data-testid={`se-card-${it.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-bold text-slate-900">{it.shift_date}</div>
                  <div className="text-xs text-slate-500">{it.operator_name}</div>
                </div>
                <Truck className="w-5 h-5 text-slate-400" />
              </div>
              {it.primary_machine && (
                <div className="text-xs text-slate-700 mb-1">
                  <span className="font-semibold uppercase text-slate-500">Primary:</span> {it.primary_machine.rego || it.primary_machine.name}
                </div>
              )}
              {it.secondary_machines && it.secondary_machines.length > 0 && (
                <div className="text-xs text-slate-700 mb-1">
                  <span className="font-semibold uppercase text-slate-500">Also used:</span>
                  <ul className="ml-3 list-disc">
                    {it.secondary_machines.map(m => <li key={m.id}>{m.rego || m.name} {m.type ? `(${m.type})` : ""}</li>)}
                  </ul>
                </div>
              )}
              {it.notes && <div className="text-xs italic text-slate-500 mt-2">{it.notes}</div>}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-md shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="border-b-2 border-slate-200 p-5 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold">New Multi-Machine Shift</h2>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-700"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={form.shift_date} onChange={(e) => setForm({...form, shift_date: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="se-date" />
                <select value={form.job_id} onChange={(e) => setForm({...form, job_id: e.target.value})} className="border-2 border-slate-200 rounded px-3 py-2" data-testid="se-job">
                  <option value="">-- Job --</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{j.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Primary machine *</label>
                <select value={form.primary_machine_id} onChange={(e) => setForm({...form, primary_machine_id: e.target.value})} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="se-primary">
                  <option value="">-- Select --</option>
                  {machines.map(m => <option key={m.id} value={m.id}>{m.rego || m.name}{m.type ? ` (${m.type})` : ""}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Also used (tick all that apply)</label>
                <div className="space-y-1 max-h-48 overflow-y-auto border-2 border-slate-200 rounded p-2">
                  {machines.filter(m => m.id !== form.primary_machine_id).map(m => (
                    <label key={m.id} className="flex items-center gap-2 cursor-pointer text-sm py-1 hover:bg-slate-50 px-1 rounded" data-testid={`se-sec-${m.id}`}>
                      <input type="checkbox" checked={form.secondary_machine_ids.includes(m.id)} onChange={() => toggleSecondary(m.id)} className="w-4 h-4" />
                      <span>{m.rego || m.name}{m.type ? ` (${m.type})` : ""}</span>
                    </label>
                  ))}
                  {machines.length === 0 && <div className="text-xs text-slate-400 italic">No machines available</div>}
                </div>
              </div>

              <textarea placeholder="Notes…" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} className="w-full border-2 border-slate-200 rounded px-3 py-2" />
            </div>
            <div className="border-t-2 border-slate-200 p-5 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowCreate(false)} disabled={saving} className="flex-1 px-4 py-3 border-2 border-slate-200 hover:border-slate-400 rounded-md font-semibold">Cancel</button>
              <button onClick={submit} disabled={saving} className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-md font-semibold" data-testid="se-submit">
                {saving ? "Saving…" : "Save Shift"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftEquipmentPage;
